/**
 * Location Library Page
 *
 * Manages per-client location/scene presets.
 * Locations are imported from Google Sheets (download as CSV or paste content).
 * Mirrors the ProductLibrary pattern — owner-only access.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../../components/TopBar';
import { useTenant } from '../../contexts/TenantContext';
import { locationService, LocationImportItem, LocationImportResult } from '../../services/locationService';
import { Location } from '../../contexts/StudioContext';

/** Route any URL through the server-side image proxy to avoid Google Drive auth restrictions. */
const proxyImageUrl = (apiBaseUrl: string, url: string) =>
  `${apiBaseUrl}/api/image-proxy?url=${encodeURIComponent(url)}`;

interface LocationLibraryProps {
  tenantId: string;
  apiBaseUrl: string;
  authToken: string;
}

/**
 * Normalise an image URL so it can be used directly as an <img src>.
 * Google Drive "share" links (/file/d/ID/view) are rewritten to the
 * direct-download form (/uc?export=view&id=ID) which browsers can load.
 */
function normalizeImageUrl(url: string): string {
  if (!url) return url;
  // Convert any Google Drive sharing/viewer URL to the thumbnail API URL,
  // which reliably returns a raw image for publicly shared files.
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/?]+)/);
  if (driveMatch) {
    return `https://drive.google.com/thumbnail?id=${driveMatch[1]}&sz=w400`;
  }
  // Also handle already-converted uc?export=view URLs
  const ucMatch = url.match(/drive\.google\.com\/uc\?.*[?&]id=([^&]+)/);
  if (ucMatch) {
    return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w400`;
  }
  return url;
}

/** Minimal but correct CSV parser — handles double-quoted fields with embedded commas */
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const row: string[] = [];
    let i = 0;
    while (i < line.length) {
      if (line[i] === '"') {
        i++;
        let field = '';
        while (i < line.length) {
          if (line[i] === '"' && line[i + 1] === '"') { field += '"'; i += 2; }
          else if (line[i] === '"') { i++; break; }
          else { field += line[i]; i++; }
        }
        row.push(field);
        if (i < line.length && line[i] === ',') i++;
      } else {
        const end = line.indexOf(',', i);
        if (end === -1) { row.push(line.slice(i).trim()); break; }
        else { row.push(line.slice(i, end).trim()); i = end + 1; }
      }
    }
    rows.push(row);
  }
  return rows;
}

/** Convert parsed CSV rows to LocationImportItem[], skipping the header row */
function csvRowsToLocations(rows: string[][]): LocationImportItem[] {
  return rows
    .filter(row => {
      // Skip header row (first column is literally "Title")
      if (row[0]?.toLowerCase() === 'title') return false;
      return row.length >= 3 && row[0]?.trim();
    })
    .map(row => ({
      title:       row[0]?.trim() || '',
      description: row[1]?.trim() || '',
      comfyText:   row[2]?.trim() || '',
      ...(row[3]?.trim() ? { thumbnail: normalizeImageUrl(row[3].trim()) } : {}),
    }));
}

export const LocationLibrary: React.FC<LocationLibraryProps> = ({ tenantId, apiBaseUrl, authToken }) => {
  const navigate = useNavigate();
  const { currentTenant, currentUser } = useTenant();

  const [locations, setLocations]         = useState<Location[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [importing, setImporting]         = useState(false);
  const [importResult, setImportResult]   = useState<LocationImportResult | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [deleting, setDeleting]           = useState(false);
  const [deletingAll, setDeletingAll]     = useState(false);

  // Client filter
  const [clientId, setClientId]   = useState('');
  const [clients, setClients]     = useState<string[]>([]);

  // Import sources
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [csvPaste, setCsvPaste]   = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [sheetUrl, setSheetUrl]   = useState('');
  const [showUrl, setShowUrl]     = useState(false);

  useEffect(() => {
    locationService.setAccessToken(authToken);
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, clientId]);

  const loadClients = async () => {
    try {
      const list = await locationService.listClients();
      setClients(list);
    } catch { /* non-critical */ }
  };

  const fetchLocations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await locationService.listLocations({ clientId: clientId || undefined });
      setLocations(response.locations || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runImport = async (rows: LocationImportItem[]) => {
    if (rows.length === 0) { setError('No valid location rows found to import.'); return; }
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const result = await locationService.importLocations(rows, clientId || undefined);
      setImportResult(result);
      await fetchLocations();
      await loadClients();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    if (fileInputRef.current) fileInputRef.current.value = '';
    const rows = csvRowsToLocations(parseCSV(text));
    await runImport(rows);
  };

  const handleImportPaste = async () => {
    const rows = csvRowsToLocations(parseCSV(csvPaste));
    await runImport(rows);
    setCsvPaste('');
    setShowPaste(false);
  };

  const handleImportFromUrl = async () => {
    if (!sheetUrl.trim()) return;
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const result = await locationService.importFromUrl(sheetUrl.trim(), clientId || undefined);
      setImportResult(result);
      setSheetUrl('');
      setShowUrl(false);
      await fetchLocations();
      await loadClients();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleDelete = async (loc: Location) => {
    if (!window.confirm(`Delete "${loc.title}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await locationService.deleteLocation(loc.locationId, loc.clientId);
      setSelectedLocation(null);
      await fetchLocations();
    } catch (err: any) { setError(err.message); }
    finally { setDeleting(false); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`Delete ALL locations${clientId ? ` for client "${clientId}"` : ''}? This cannot be undone.`)) return;
    setDeletingAll(true);
    setError(null);
    setSuccessMessage(null);
    setImportResult(null);
    try {
      const deleted = await locationService.deleteAllLocations(clientId || undefined);
      setLocations([]);
      setSelectedLocation(null);
      setSuccessMessage(`${deleted} location(s) deleted.`);
      await loadClients();
    } catch (err: any) { setError(err.message); }
    finally { setDeletingAll(false); }
  };

  const bannerBase = 'relative px-4 py-3 rounded-lg mb-4 text-body flex items-start justify-between gap-3';
  const dismissBtn = 'flex-shrink-0 text-lg leading-none opacity-60 hover:opacity-100 transition-opacity duration-fast';

  return (
    <div className="min-h-screen bg-surface-app">
      <TopBar tenantName={currentTenant?.name || 'Workspace'} userEmail={currentUser?.email || ''} />

      <div className="px-8 py-6 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => navigate('/workflows')}
              className="text-label text-gray-500 hover:text-gray-900 transition-colors duration-fast">
              ← Workflows
            </button>
            <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Location Library</h1>
          </div>
          <div className="flex items-center gap-3">
            {locations.length > 0 && (
              <button type="button" onClick={handleDeleteAll} disabled={deletingAll || importing}
                className="px-4 py-2.5 bg-error-light text-error border border-error/30 rounded-md text-label font-medium hover:bg-red-100 transition-colors duration-fast disabled:opacity-50">
                {deletingAll ? 'Deleting…' : 'Delete All'}
              </button>
            )}
            <button type="button" onClick={() => { setShowUrl(v => !v); setShowPaste(false); }} disabled={importing}
              className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-md text-label font-medium hover:bg-gray-50 transition-colors duration-fast shadow-sm disabled:opacity-50">
              Import from URL
            </button>
            <button type="button" onClick={() => { setShowPaste(v => !v); setShowUrl(false); }} disabled={importing}
              className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-md text-label font-medium hover:bg-gray-50 transition-colors duration-fast shadow-sm disabled:opacity-50">
              Paste CSV
            </button>
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={importing}
              className="px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold transition-colors duration-fast shadow-sm disabled:opacity-50">
              {importing ? 'Importing…' : 'Import CSV'}
            </button>
            <input ref={fileInputRef} type="file" accept=".csv,.txt" aria-label="Import locations CSV file"
              className="hidden" onChange={handleImportFile} />
          </div>
        </div>

        {/* Client filter */}
        <div className="flex gap-3 items-center mb-5">
          <select aria-label="Filter by client" value={clientId} onChange={e => { setClientId(e.target.value); setSelectedLocation(null); }}
            className="px-4 py-2.5 border border-gray-300 rounded-md text-body bg-white outline-none focus:border-blue-400 transition-colors duration-fast select-input w-64">
            <option value="">All clients (tenant-wide)</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <span className="text-label text-gray-500 whitespace-nowrap">
            {locations.length} location{locations.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Banners */}
        {importResult && (
          <div className={`${bannerBase} ${importResult.errors.length > 0 ? 'bg-warning-light text-warning-dark border border-warning/30' : 'bg-success-light text-success-dark border border-success/30'}`}>
            <div>
              <strong>Import complete:</strong> {importResult.imported} location{importResult.imported !== 1 ? 's' : ''} imported.
              {importResult.errors.length > 0 && (
                <ul className="list-disc list-inside mt-1 text-label">{importResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
              )}
            </div>
            <button type="button" className={dismissBtn} onClick={() => setImportResult(null)}>×</button>
          </div>
        )}
        {successMessage && (
          <div className={`${bannerBase} bg-success-light text-success-dark border border-success/30`}>
            <span>{successMessage}</span>
            <button type="button" className={dismissBtn} onClick={() => setSuccessMessage(null)}>×</button>
          </div>
        )}
        {error && (
          <div className={`${bannerBase} bg-error-light text-error-dark border border-error/30`}>
            <span>{error}</span>
            <button type="button" className={dismissBtn} onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Import from URL panel */}
        {showUrl && (
          <div className="mb-5 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
            <p className="text-label text-gray-600 mb-1">
              Paste a Google Sheets link — the sheet must be shared as <strong>"Anyone with the link can view"</strong>.
            </p>
            <p className="text-meta text-gray-400 mb-3">
              Columns expected: <code className="text-xs bg-gray-100 px-1 rounded">Title, Description, ComfyUI text</code>
            </p>
            <input
              type="url"
              value={sheetUrl}
              onChange={e => setSheetUrl(e.target.value)}
              placeholder="https://docs.google.com/spreadsheets/d/…"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-body outline-none focus:border-blue-400 transition-colors duration-fast"
            />
            <div className="flex gap-3 mt-3">
              <button type="button" onClick={handleImportFromUrl} disabled={!sheetUrl.trim() || importing}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold disabled:opacity-50 transition-colors duration-fast">
                {importing ? 'Importing…' : 'Import'}
              </button>
              <button type="button" onClick={() => { setShowUrl(false); setSheetUrl(''); }}
                className="px-4 py-2 border border-gray-300 rounded-md text-label text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Paste CSV panel */}
        {showPaste && (
          <div className="mb-5 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
            <p className="text-label text-gray-600 mb-2">
              Paste CSV from Google Sheets — columns: <code className="text-xs bg-gray-100 px-1 rounded">Title, Description, ComfyUI text</code>
            </p>
            <textarea value={csvPaste} onChange={e => setCsvPaste(e.target.value)} rows={8}
              placeholder="Title,Description,ComfyUI text&#10;Boutique Hotel Lobby,&quot;A refined...&quot;,&quot;boutique hotel lobby...&quot;"
              className="w-full font-mono text-sm border border-gray-300 rounded-md px-3 py-2 outline-none focus:border-blue-400 resize-y" />
            <div className="flex gap-3 mt-3">
              <button type="button" onClick={handleImportPaste} disabled={!csvPaste.trim() || importing}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold disabled:opacity-50">
                {importing ? 'Importing…' : 'Import'}
              </button>
              <button type="button" onClick={() => { setShowPaste(false); setCsvPaste(''); }}
                className="px-4 py-2 border border-gray-300 rounded-md text-label text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Main layout */}
        <div className="flex gap-6 min-h-0">
          {/* Location list */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="py-16 text-center text-body text-gray-400">Loading locations…</div>
            ) : locations.length === 0 ? (
              <div className="py-16 text-center">
                <div className="text-5xl mb-4">🗺️</div>
                <p className="text-body text-gray-500 mb-5">No locations imported yet{clientId ? ` for client "${clientId}"` : ''}.</p>
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-label font-semibold transition-colors duration-fast">
                  Import CSV
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {locations.map(loc => {
                  const isSelected = selectedLocation?.locationId === loc.locationId;
                  return (
                    <button key={loc.locationId} type="button" onClick={() => setSelectedLocation(isSelected ? null : loc)}
                      className={`w-full text-left rounded-lg border-2 transition-all duration-base shadow-xs flex items-start gap-3 ${
                        isSelected ? 'border-blue-500 bg-blue-50 shadow-focus p-3' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm p-3'
                      }`}>
                      {loc.thumbnail && (
                        <img
                          src={proxyImageUrl(apiBaseUrl, loc.thumbnail)}
                          alt={loc.title}
                          className="w-14 h-14 rounded object-cover flex-shrink-0 bg-gray-100"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 text-label">{loc.title}</div>
                        <div className="text-meta text-gray-500 mt-1 line-clamp-2">{loc.description}</div>
                        {loc.clientId && (
                          <span className="mt-2 inline-block px-2 py-0.5 bg-green-50 text-green-700 text-meta font-medium rounded-full">{loc.clientId}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Details panel */}
          {selectedLocation && (
            <div className="w-80 flex-shrink-0 bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <h2 className="text-body font-semibold text-gray-900">Details</h2>
                <button type="button" onClick={() => setSelectedLocation(null)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 text-lg leading-none">×</button>
              </div>
              <div className="p-4 overflow-y-auto flex-1 space-y-3">
                {selectedLocation.thumbnail && (
                  <div>
                    <div className="text-meta font-medium text-gray-500 mb-2">Thumbnail</div>
                    <img
                      src={proxyImageUrl(apiBaseUrl, selectedLocation.thumbnail)}
                      alt={selectedLocation.title}
                      className="w-full rounded-lg object-cover bg-gray-100 max-h-40"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                )}
                <div>
                  <div className="text-meta font-medium text-gray-500 mb-1">Title</div>
                  <div className="text-label text-gray-900">{selectedLocation.title}</div>
                </div>
                <div>
                  <div className="text-meta font-medium text-gray-500 mb-1">Description</div>
                  <div className="text-label text-gray-700 leading-relaxed">{selectedLocation.description}</div>
                </div>
                <div>
                  <div className="text-meta font-medium text-gray-500 mb-1">ComfyUI Text</div>
                  <div className="text-meta text-gray-600 leading-relaxed font-mono bg-gray-50 rounded p-2">{selectedLocation.comfyText}</div>
                </div>
                {selectedLocation.clientId && (
                  <div>
                    <div className="text-meta font-medium text-gray-500 mb-1">Client</div>
                    <div className="text-label text-gray-900">{selectedLocation.clientId}</div>
                  </div>
                )}
                <div>
                  <div className="text-meta font-medium text-gray-500 mb-1">Imported</div>
                  <div className="text-label text-gray-900">{new Date(selectedLocation.importedAt).toLocaleDateString()}</div>
                </div>
                <div className="pt-2">
                  <button type="button" onClick={() => handleDelete(selectedLocation)} disabled={deleting}
                    className="w-full px-4 py-2.5 bg-error-light text-error border border-error/30 rounded-md text-label font-medium hover:bg-red-100 transition-colors duration-fast disabled:opacity-50">
                    {deleting ? 'Deleting…' : 'Delete Location'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationLibrary;

